import React, { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useChatContext } from "../../contexts/ChatContext";
import { TaskStatus, TaskType, useTasksContext } from "../../contexts/TasksContext";
import type { Task } from "../../contexts/TasksContext";
import { Thread as ThreadType } from "../../types/types";
import styles from "./Threads.module.scss";
import useIsMobile from "../../hooks/useIsMobile";

function startOfDayMs(d: Date): number {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
}

function dayGroupLabel(updatedAt: Date, now = new Date()): string {
  if (startOfDayMs(updatedAt) === startOfDayMs(now)) return "Today";
  const y = new Date(now);
  y.setDate(y.getDate() - 1);
  if (startOfDayMs(updatedAt) === startOfDayMs(y)) return "Yesterday";
  if (updatedAt.getTime() >= startOfDayMs(now) - 7 * 86400000) return "This week";
  return "Earlier";
}

function resolveModeLabel(raw?: string): string {
  const p = String(raw || "")
    .trim()
    .toLowerCase();
  if (p === "mydeal") return "Deal";
  if (p === "market") return "Market";
  if (p === "legal") return "Legal";
  if (p === "oreima") return "Oreima";
  return "—";
}

function taskCardTitle(task: Task): string {
  switch (task.type) {
    case TaskType.LEGAL_SEARCH:
      return `${resolveModeLabel(task.page ?? task.mode)} search`;
    case TaskType.UPLOAD:
      return "Upload & summary";
    case TaskType.SUMMARY:
      return "Summary";
    default:
      return "Task";
  }
}

function shouldUseDerivedThreadTitle(threadTitle: string, task: Task): boolean {
  const t = String(threadTitle ?? "").trim();
  if (!t) return true;
  if (t === "Conversation") return true;
  if (t === task.file.filename) return true;
  return false;
}

interface ThreadProps {
  thread: ThreadType;
  displayTitle: string;
  setSelectedMenu: (menu: number) => void;
  setMenuVisibility: (visibility: boolean) => void;
  hasActiveTask?: boolean;
  activeTaskPercent?: number | null;
}

const Thread: React.FC<ThreadProps> = ({
  thread,
  displayTitle,
  setSelectedMenu,
  setMenuVisibility,
  hasActiveTask = false,
  activeTaskPercent = null,
}) => {
  const { changeThread, selectedThread, deleteThread, renameThread, abortQuery } = useChatContext();
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(displayTitle);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuPlacement, setMenuPlacement] = useState<"above" | "below">("below");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const menuRef = useRef<HTMLSpanElement | null>(null);
  const deleteConfirmRef = useRef<HTMLDivElement | null>(null);
  const deleteDialogPanelRef = useRef<HTMLDivElement | null>(null);
  const isMobile = useIsMobile();

  const confirmDeleteThread = async () => {
    await deleteThread({ id: thread.id, title: thread.title });
    setShowDeleteConfirm(false);
    setIsMenuOpen(false);
  };

  const onChangeThread = (thread: ThreadType) => {
    // Politique uniforme: changer de conversation coupe le stream courant.
    abortQuery();
    changeThread(thread);
    if (isMobile) {
      setSelectedMenu(0);
      setMenuVisibility(false);
      return;
    }
    // Desktop : si l’utilisateur est dans “Chat History”, on y reste après sélection.
    setSelectedMenu(2);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedTitle(e.target.value);
  };

  const handleTitleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const newTitle = editedTitle.trim();
    const oldTitle = thread.title;
    if (newTitle && newTitle !== oldTitle) {
      await renameThread({ id: thread.id, oldTitle, newTitle });
      setEditedTitle(newTitle);
    } else {
      setEditedTitle(oldTitle);
    }
    setIsEditing(false);
  };

  useEffect(() => {
    // Si on n’est pas en train de renommer, garder le titre aligné avec la “template” affichée.
    if (isEditing) return;
    setEditedTitle(displayTitle);
  }, [displayTitle, isEditing]);

  // Fermer au clic extérieur (la modale est en portal : elle n’est pas dans menuRef)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const t = event.target as Node;
      if (menuRef.current?.contains(t)) return;
      if (deleteConfirmRef.current?.contains(t)) return;
      setIsMenuOpen(false);
      setShowDeleteConfirm(false);
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Fermer tous les menus quand on en ouvre un autre
  useEffect(() => {
    const handleCloseAll = () => {
      setIsMenuOpen(false);
      setShowDeleteConfirm(false);
    };
    document.addEventListener("closeAllHistoryMenus", handleCloseAll as EventListener);
    return () => {
      document.removeEventListener("closeAllHistoryMenus", handleCloseAll as EventListener);
    };
  }, []);

  useEffect(() => {
    if (!showDeleteConfirm) return;
    const id = window.setTimeout(() => {
      deleteDialogPanelRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(id);
  }, [showDeleteConfirm]);

  return (
    <>
      <div
        className={`${styles.thread} ${
          selectedThread?.id === thread.id ? styles.threadActive : ""
        } ${isMenuOpen || isEditing ? styles.threadMenuOpen : ""} d-flex justify-content-between align-items-center`}
        onClick={() => !isEditing && onChangeThread(thread)}
      >
        {isEditing ? (
          <form onSubmit={handleTitleSubmit} className="w-100" onClick={e => e.stopPropagation()}>
            <input
              type="text"
              value={editedTitle}
              onChange={handleTitleChange}
              autoFocus
              onBlur={() => {
                setEditedTitle(displayTitle);
                setIsEditing(false);
              }}
              className={styles.threadTitleInput}
            />
          </form>
        ) : (
          <>
            <span className={styles.threadTitle}>
              <span className={styles.threadTitlePreview}>{displayTitle}</span>
              {hasActiveTask && (
                <span
                  style={{
                    marginLeft: 8,
                    fontSize: 11,
                    color: "#6c757d",
                    fontStyle: "italic",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  {`In progress${typeof activeTaskPercent === "number" ? ` · ${activeTaskPercent}%` : "…"}`}
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                    style={{ width: 10, height: 10, borderWidth: 2 }}
                  />
                </span>
              )}
            </span>
            <span ref={menuRef} className={styles.historyMenuWrapper}>
              <i
                className={`bi bi-three-dots ${styles.historyMenuIcon}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isMenuOpen) {
                    setIsMenuOpen(false);
                  } else {
                    // calcul au-dessus / en dessous
                    // IMPORTANT: la liste historique est scrollable et peut clipper les popups.
                    // On se base d'abord sur l'espace visible dans le conteneur scroll parent,
                    // puis sur le viewport en fallback.
                    if (menuRef.current) {
                      const rect = menuRef.current.getBoundingClientRect();
                      const scrollParent = menuRef.current.closest(
                        "[data-history-scroll], .overflow-y-scroll, .overflow-auto"
                      ) as HTMLElement | null;
                      const containerBottom = scrollParent
                        ? scrollParent.getBoundingClientRect().bottom
                        : window.innerHeight || document.documentElement.clientHeight;
                      const spaceBelow = containerBottom - rect.bottom;
                      const approxMenuHeight = 88;
                      setMenuPlacement(spaceBelow < approxMenuHeight ? "above" : "below");
                    }
                    document.dispatchEvent(new Event("closeAllHistoryMenus"));
                    setIsMenuOpen(true);
                  }
                }}
              />
              {isMenuOpen && (
                <div
                  className={`${styles.historyMenu} ${
                    menuPlacement === "above" ? styles.historyMenuAbove : styles.historyMenuBelow
                  }`}
                >
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsMenuOpen(false);
                      setEditedTitle(displayTitle);
                      setIsEditing(true);
                    }}
                  >
                    <i className="bi bi-pencil me-1" /> Rename
                  </span>
                  <span
                    className={styles.historyMenuDelete}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDeleteConfirm(true);
                    }}
                  >
                    <i className="bi bi-trash me-1" /> Delete
                  </span>
                </div>
              )}
            </span>
          </>
        )}
      </div>
      {showDeleteConfirm &&
        createPortal(
          <div
            ref={deleteConfirmRef}
            className={styles.deleteConfirmOverlay}
            onClick={() => setShowDeleteConfirm(false)}
            role="presentation"
          >
            <div
              ref={deleteDialogPanelRef}
              role="alertdialog"
              aria-modal="true"
              aria-labelledby={`delete-thread-title-${thread.id}`}
              tabIndex={-1}
              className={styles.deleteConfirmDialog}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.stopPropagation();
                  void confirmDeleteThread();
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  setShowDeleteConfirm(false);
                }
              }}
            >
              <div id={`delete-thread-title-${thread.id}`} className={styles.deleteConfirmTitle}>
                Delete chat history?
              </div>
              <div className={styles.deleteConfirmText}>
                Are you sure you want to delete this chat history? This action cannot be undone.
              </div>
              <div className={styles.deleteConfirmActions}>
                <button
                  type="button"
                  className={`${styles.deleteConfirmButton} ${styles.deleteConfirmButtonCancel}`}
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`${styles.deleteConfirmButton} ${styles.deleteConfirmButtonDelete}`}
                  onClick={() => void confirmDeleteThread()}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

interface ThreadsProps {
  setSelectedMenu: (menu: number) => void;
  setMenuVisibility: (visibility: boolean) => void;
}

const Threads: React.FC<ThreadsProps> = ({ setSelectedMenu, setMenuVisibility }) => {
  const { threads, activeStreamThreadIds } = useChatContext();
  const { tasks } = useTasksContext();
  const [search, setSearch] = useState<string>("");

  const taskByThreadIdLatest = useMemo(() => {
    const m = new Map<string, Task>();
    tasks.forEach((t) => {
      if (!t.threadId) return;
      const tid = String(t.threadId);
      const prev = m.get(tid);
      if (!prev) {
        m.set(tid, t);
        return;
      }
      // Nouveau par créé (fall-back updatedAt)
      if ((t.createdAt ?? 0) >= (prev.createdAt ?? 0)) {
        m.set(tid, t);
      }
    });
    return m;
  }, [tasks]);

  const getThreadDisplayTitle = useMemo(() => {
    return (thread: ThreadType): string => {
      const task = taskByThreadIdLatest.get(String(thread.id));
      if (!task) return thread.title;
      const derived = taskCardTitle(task);
      return shouldUseDerivedThreadTitle(thread.title, task) ? derived : thread.title;
    };
  }, [taskByThreadIdLatest]);

  const filteredThreads = threads.filter((thread) =>
    search
      ? getThreadDisplayTitle(thread).toLowerCase().includes(search.toLowerCase())
      : true
  );

  const sortedThreads = useMemo(
    () =>
      [...filteredThreads].sort(
        (a, b) => b.updatedAt.getTime() - a.updatedAt.getTime()
      ),
    [filteredThreads]
  );

  const historyRows = useMemo(() => {
    const activeTaskThreadIds = new Set(
      tasks
        .filter(
          (t) =>
            t.type === TaskType.LEGAL_SEARCH &&
            Boolean(t.threadId) &&
            t.status !== TaskStatus.COMPLETED &&
            t.status !== TaskStatus.CANCELLED &&
            t.status !== TaskStatus.ERROR
        )
        .map((t) => String(t.threadId))
    );
    const activeStreamIds = new Set(
      (activeStreamThreadIds ?? []).map((id) => String(id))
    );
    const activeTaskPercentByThread = new Map<string, number>();
    tasks.forEach((t) => {
      if (
        t.type !== TaskType.LEGAL_SEARCH ||
        !t.threadId ||
        t.status === TaskStatus.COMPLETED ||
        t.status === TaskStatus.CANCELLED ||
        t.status === TaskStatus.ERROR
      ) {
        return;
      }
      const total = Math.max(1, t.file.nbPages || 1);
      const processed = Math.max(0, Math.min(t.file.nbProcessed || 0, total));
      const pct = Math.round((processed / total) * 100);
      activeTaskPercentByThread.set(String(t.threadId), pct);
    });

    const rows: React.ReactNode[] = [];
    let lastGroup: string | null = null;
    sortedThreads.forEach((thread) => {
      const g = dayGroupLabel(thread.updatedAt);
      if (g !== lastGroup) {
        rows.push(
          <div key={`day-${g}-${thread.id}`} className={styles.dayGroupHeader}>
            {g}
          </div>
        );
        lastGroup = g;
      }
      rows.push(
        <Thread
          key={thread.id}
          thread={thread}
          displayTitle={getThreadDisplayTitle(thread)}
          hasActiveTask={
            activeTaskThreadIds.has(thread.id) || activeStreamIds.has(thread.id)
          }
          activeTaskPercent={activeTaskPercentByThread.get(thread.id) ?? null}
          setSelectedMenu={setSelectedMenu}
          setMenuVisibility={setMenuVisibility}
        />
      );
    });
    return rows;
  }, [
    sortedThreads,
    tasks,
    activeStreamThreadIds,
    setSelectedMenu,
    setMenuVisibility,
  ]);

  return (
    <div className={styles.historyPanel}>
      <div className={styles.historyHeader}>
        <h6 className="mb-1 px-1">History</h6>
        <input
          className={`${styles.searchInput} w-100`}
          onChange={(event) => setSearch(event.target.value)}
          value={search}
          placeholder="Search history…"
          autoComplete="off"
          type="search"
          aria-label="Search conversations"
        />
      </div>
      <div
        className={styles.historyList}
        data-history-scroll
        role="region"
        aria-label="Conversation list"
      >
        {threads.length === 0 ? (
          <div className="align-self-center small py-3 text-center px-2 text-muted">
            <i className="bi bi-info-circle me-1" aria-hidden />
            No conversations yet.
          </div>
        ) : sortedThreads.length === 0 && search.trim() ? (
          <div className="small py-3 text-center px-2 text-muted">
            <i className="bi bi-search me-1" aria-hidden />
            No conversation matches &ldquo;{search.trim()}&rdquo;.
          </div>
        ) : (
          historyRows
        )}
      </div>
    </div>
  );
};

export default Threads;
